import { useMemo } from "react"
import { useSearchParams } from "react-router-dom"
import toast from "react-hot-toast"

import { Pagination } from "components"
import OutletFilterSelect from "components/forms/outlet-filter-select"
import { getDefaultOutletFilterValue, resolveOutletFilter } from "helpers/general-helper"
import useConfirmedOrderReportRepositories from "repositories/confirmed-order-report"

const formatNumber = (value) => Number(value ?? 0).toLocaleString("id-ID")

const formatCurrency = (value) => Number(value ?? 0).toLocaleString("id-ID", {
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
})

const formatDateTime = (value) => {
  if (!value) return "-"
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return String(value)

  return date.toLocaleString("id-ID", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  })
}

const toInputDate = (value) => {
  const date = value ? new Date(value) : new Date()
  if (Number.isNaN(date.getTime())) return ""

  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, "0")
  const day = String(date.getDate()).padStart(2, "0")

  return `${year}-${month}-${day}`
}

const SourceBadge = ({ source }) => {
  const label = source || "-"
  const className = label === "YOIBOI"
    ? "bg-sky-50 text-sky-700 border-sky-200"
    : "bg-neutral-100 text-neutral-700 border-neutral-200"

  return (
    <span className={`inline-flex items-center px-2 py-1 text-xs font-semibold border rounded ${className}`}>
      {label}
    </span>
  )
}

export default function List() {
  const [searchParams, setSearchParams] = useSearchParams()

  const dateFrom = searchParams.get("date_from") || toInputDate(new Date(Date.now() - 86400000))
  const dateTo = searchParams.get("date_to") || toInputDate(new Date(Date.now() - 86400000))
  const outletId = searchParams.get("outlet_id") || getDefaultOutletFilterValue()
  const { outlet_id_csv: resolvedOutletCsv } = resolveOutletFilter(outletId)
  const page = searchParams.get("page") || "1"
  const perPage = searchParams.get("per_page") || "20"

  const { data: reportData, isLoading } = useConfirmedOrderReportRepositories({
    date_from: dateFrom,
    date_to: dateTo,
    outlet_id: resolvedOutletCsv || undefined,
    page,
    per_page: perPage,
    enabled: true,
  })

  const rows = useMemo(() => {
    if (Array.isArray(reportData?.items?.data)) return reportData.items.data
    return reportData?.rows ?? []
  }, [reportData])
  const summary = useMemo(() => reportData?.summary ?? {}, [reportData])
  const pagination = reportData?.items
  const paginationLinks = useMemo(() => {
    const links = pagination?.links ?? []

    return links.map((link) => {
      if (!link?.url) return link

      const nextParams = new URLSearchParams(searchParams)

      try {
        const url = new URL(link.url, window.location.origin)
        const pageNumber = url.searchParams.get("page")

        if (pageNumber) {
          nextParams.set("page", pageNumber)
        }
      } catch (error) {
        const urlParams = new URLSearchParams(String(link.url).split("?")[1] ?? "")
        const pageNumber = urlParams.get("page")

        if (pageNumber) {
          nextParams.set("page", pageNumber)
        }
      }

      return {
        ...link,
        url: `?${nextParams.toString()}`,
      }
    })
  }, [pagination, searchParams])

  const paginationFrom = useMemo(() => {
    if (pagination?.from) return pagination.from
    if (!pagination || Number(pagination.total ?? 0) === 0) return 0

    return (Number(pagination.current_page ?? 1) - 1) * Number(pagination.per_page ?? perPage) + 1
  }, [pagination, perPage])

  const paginationTo = useMemo(() => {
    if (pagination?.to) return pagination.to
    if (!pagination) return 0

    return Math.min(
      Number(pagination.current_page ?? 1) * Number(pagination.per_page ?? perPage),
      Number(pagination.total ?? 0)
    )
  }, [pagination, perPage])

  const mergeSearchParams = (nextValues) => {
    const nextParams = new URLSearchParams(searchParams)

    Object.entries(nextValues).forEach(([key, value]) => {
      if (value === null || value === undefined || value === "") {
        nextParams.delete(key)
      } else {
        nextParams.set(key, value)
      }
    })

    setSearchParams(nextParams)
  }

  const handleExport = () => {
    if (!dateFrom || !dateTo) {
      toast.error("Periode wajib diisi sebelum export")
      return
    }

    const query = new URLSearchParams({
      date_from: dateFrom,
      date_to: dateTo,
      outlet_id: resolvedOutletCsv,
    })

    window.open(
      `${process.env.REACT_APP_INVENTORY_API_URL}/reports/confirmed-order-report/export?${query.toString()}`,
      "_blank"
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 p-5 bg-white border border-neutral-200 rounded-2xl lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h1 className="text-lg font-semibold text-neutral-900">Faktur Belum Lunas</h1>
          <p className="mt-1 text-sm text-neutral-500">
            Daftar faktur yang belum lunas.
          </p>
        </div>

        <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
          <div className="min-w-[220px]">
            <label className="block mb-2 text-xs font-semibold tracking-wide text-neutral-500">
              Outlet
            </label>
            <OutletFilterSelect
              value={outletId}
              onChange={(v) => mergeSearchParams({ outlet_id: v, page: "1" })}
              className="w-full px-3 py-2 border rounded-lg border-neutral-300 focus:outline-none focus:ring-2 focus:ring-neutral-800"
              title="Outlet"
            />
          </div>

          <div className="min-w-[160px]">
            <label className="block mb-2 text-xs font-semibold tracking-wide text-neutral-500">
              Tanggal Dari
            </label>
            <input
              type="date"
              className="w-full px-3 py-2 border rounded-lg border-neutral-300 focus:outline-none focus:ring-2 focus:ring-neutral-800"
              value={dateFrom}
              onChange={(event) => mergeSearchParams({ date_from: event.target.value, page: "1" })}
            />
          </div>

          <div className="min-w-[160px]">
            <label className="block mb-2 text-xs font-semibold tracking-wide text-neutral-500">
              Tanggal Sampai
            </label>
            <input
              type="date"
              className="w-full px-3 py-2 border rounded-lg border-neutral-300 focus:outline-none focus:ring-2 focus:ring-neutral-800"
              value={dateTo}
              onChange={(event) => mergeSearchParams({ date_to: event.target.value, page: "1" })}
            />
          </div>

          <div className="min-w-[120px]">
            <label className="block mb-2 text-xs font-semibold tracking-wide text-neutral-500">
              Per Halaman
            </label>
            <select
              value={perPage}
              onChange={(event) => mergeSearchParams({ per_page: event.target.value, page: "1" })}
              className="w-full px-3 py-2 border rounded-lg border-neutral-300 focus:outline-none focus:ring-2 focus:ring-neutral-800"
            >
              {[10, 20, 50, 100, 200].map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </select>
          </div>

          <button
            type="button"
            onClick={handleExport}
            className="px-4 py-2 text-sm font-semibold text-white rounded-lg bg-brand-900 hover:bg-brand-800"
          >
            Ekspor Excel
          </button>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-3 xl:grid-cols-5">
        <div className="p-4 bg-white border border-neutral-200 rounded-2xl">
          <div className="text-xs font-semibold tracking-wide text-neutral-500">Total Order</div>
          <div className="mt-2 text-2xl font-semibold text-neutral-900">{formatNumber(summary.total_orders)}</div>
        </div>
        <div className="p-4 bg-white border border-neutral-200 rounded-2xl">
          <div className="text-xs font-semibold tracking-wide text-neutral-500">Atalla</div>
          <div className="mt-2 text-2xl font-semibold text-neutral-900">{formatNumber(summary.atalla_total_orders)}</div>
        </div>
        <div className="p-4 bg-white border border-neutral-200 rounded-2xl">
          <div className="text-xs font-semibold tracking-wide text-neutral-500">Total Penjualan</div>
          <div className="mt-2 text-2xl font-semibold text-neutral-900">{formatCurrency(summary.total_sales_amount)}</div>
        </div>
        <div className="p-4 bg-white border border-neutral-200 rounded-2xl">
          <div className="text-xs font-semibold tracking-wide text-neutral-500">Pelunasan DN</div>
          <div className="mt-2 text-2xl font-semibold text-neutral-900">{formatCurrency(summary.total_debit_note_settlement_amount)}</div>
        </div>
        <div className="p-4 bg-white border border-neutral-200 rounded-2xl">
          <div className="text-xs font-semibold tracking-wide text-neutral-500">Qty</div>
          <div className="mt-2 text-2xl font-semibold text-neutral-900">{formatNumber(summary.total_qty_sold)}</div>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <div className="p-4 bg-white border border-neutral-200 rounded-2xl">
          <div className="text-xs font-semibold tracking-wide text-neutral-500">Order ID Pertama</div>
          <div className="mt-2 font-semibold break-all text-neutral-900">{summary.first_order_id || "-"}</div>
          <div className="mt-1 text-xs text-neutral-500">{summary.first_order_source || "-"} · {formatDateTime(summary.first_order_date)}</div>
        </div>
        <div className="p-4 bg-white border border-neutral-200 rounded-2xl">
          <div className="text-xs font-semibold tracking-wide text-neutral-500">Order ID Terakhir</div>
          <div className="mt-2 font-semibold break-all text-neutral-900">{summary.last_order_id || "-"}</div>
          <div className="mt-1 text-xs text-neutral-500">{summary.last_order_source || "-"} · {formatDateTime(summary.last_order_date)}</div>
        </div>
      </div>

      <div className="overflow-hidden bg-white border border-neutral-200 rounded-2xl">
        <div className="px-4 py-4 border-b border-neutral-200 bg-neutral-50">
          <div className="text-sm font-semibold text-neutral-900">{reportData?.branch_name || "SEMUA OUTLET"}</div>
          <div className="mt-1 text-xs text-neutral-500">{reportData?.period_label || "-"}</div>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-neutral-100">
              <tr>
                <th className="px-4 py-3 font-semibold text-left text-neutral-700">SUMBER</th>
                <th className="px-4 py-3 font-semibold text-left text-neutral-700">ID ORDER</th>
                <th className="px-4 py-3 font-semibold text-left text-neutral-700">NO. ORDER</th>
                <th className="px-4 py-3 font-semibold text-left text-neutral-700">TGL ORDER</th>
                <th className="px-4 py-3 font-semibold text-left text-neutral-700">OUTLET</th>
                <th className="px-4 py-3 font-semibold text-left text-neutral-700">CUSTOMER</th>
                <th className="px-4 py-3 font-semibold text-left text-neutral-700">STATUS</th>
                <th className="px-4 py-3 font-semibold text-right text-neutral-700">NETT</th>
                <th className="px-4 py-3 font-semibold text-right text-neutral-700">PELUNASAN DN</th>
                <th className="px-4 py-3 font-semibold text-right text-neutral-700">PENJUALAN</th>
                <th className="px-4 py-3 font-semibold text-right text-neutral-700">QTY</th>
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr>
                  <td colSpan={11} className="px-4 py-10 text-center text-neutral-500">
                    Memuat data laporan...
                  </td>
                </tr>
              )}

              {!isLoading && rows.length === 0 && (
                <tr>
                  <td colSpan={11} className="px-4 py-10 text-center text-neutral-500">
                    Tidak ada faktur belum lunas untuk periode yang dipilih.
                  </td>
                </tr>
              )}

              {!isLoading && rows.map((row, index) => (
                <tr key={row.row_key || `${row.source}-${row.order_id}-${index}`} className="border-t border-neutral-200 hover:bg-brand-50 transition-colors">
                  <td className="px-4 py-3 whitespace-nowrap"><SourceBadge source={row.source} /></td>
                  <td className="px-4 py-3 font-medium break-all text-neutral-900">{row.order_id || "-"}</td>
                  <td className="px-4 py-3 whitespace-nowrap text-neutral-800">{row.order_number || "-"}</td>
                  <td className="px-4 py-3 whitespace-nowrap text-neutral-800">{formatDateTime(row.order_date)}</td>
                  <td className="px-4 py-3 whitespace-nowrap text-neutral-800">{row.branch_name || row.outlet_id || "-"}</td>
                  <td className="px-4 py-3 text-neutral-800">{row.customer_name || "-"}</td>
                  <td className="px-4 py-3 whitespace-nowrap text-neutral-800">{row.payment_status || "-"}</td>
                  <td className="px-4 py-3 text-right text-neutral-800">{formatCurrency(row.nett_amount)}</td>
                  <td className="px-4 py-3 text-right text-neutral-800">{formatCurrency(row.debit_note_settlement_amount)}</td>
                  <td className="px-4 py-3 font-semibold text-right text-neutral-900">{formatCurrency(row.sales_amount)}</td>
                  <td className="px-4 py-3 text-right text-neutral-800">{formatNumber(row.qty_sold)}</td>
                </tr>
              ))}
            </tbody>
            {!isLoading && rows.length > 0 && (
              <tfoot className="bg-neutral-50">
                <tr className="border-t border-neutral-200">
                  <td className="px-4 py-3 font-semibold text-neutral-900" colSpan={7}>GRAND TOTAL</td>
                  <td className="px-4 py-3 font-semibold text-right text-neutral-900">{formatCurrency(summary.total_nett_amount)}</td>
                  <td className="px-4 py-3 font-semibold text-right text-neutral-900">{formatCurrency(summary.total_debit_note_settlement_amount)}</td>
                  <td className="px-4 py-3 font-semibold text-right text-neutral-900">{formatCurrency(summary.total_sales_amount)}</td>
                  <td className="px-4 py-3 font-semibold text-right text-neutral-900">{formatNumber(summary.total_qty_sold)}</td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
        <div className="px-4 py-4 border-t border-neutral-200">
          <Pagination
            links={paginationLinks}
            from={paginationFrom}
            to={paginationTo}
            total={pagination?.total}
          />
        </div>
      </div>
    </div>
  )
}
